/**
 * FileUploader Component for Task Attachments
 *
 * Supports two modes:
 * 1. Edit mode (taskId provided): Uploads files immediately to S3
 * 2. Create mode (no taskId): Collects files locally for upload after task creation
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import type { TaskAttachment } from '@/types/tasks';
import { formatFileSize } from '@/types/tasks';
import {
  uploadTaskAttachmentWithProgress,
  deleteTaskAttachment,
  getAttachmentDownloadUrl,
  cancelAttachmentUpload,
  type AttachmentUploadProgress,
} from '@/services/api';

// Check if file type supports preview
const canPreview = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const previewableExtensions = [
    // Code
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs',
    'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'bash', 'sql',
    'css', 'scss', 'less', 'yaml', 'yml', 'toml', 'ini', 'conf', 'json',
    // Markdown
    'md', 'markdown',
    // Images
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico',
    // Documents
    'pdf', 'docx', 'doc', 'xlsx', 'xls', 'csv',
    // HTML
    'html', 'htm',
    // Text
    'txt', 'log',
  ];
  return previewableExtensions.includes(ext);
};

interface FileWithProgress {
  file: File;
  progress: number;
  speed: number; // bytes per second
  estimatedTimeRemaining: number; // seconds
  uploading: boolean;
  uploaded: boolean;
  error?: string;
  attachmentId?: string;
  abortController?: AbortController;
}

interface FileUploaderProps {
  taskId?: string; // If provided, uploads immediately; if not, collects files
  attachments?: TaskAttachment[]; // Existing attachments to display
  onAttachmentsChange?: (attachments: TaskAttachment[]) => void; // Called when attachments change
  onPendingFilesChange?: (files: File[]) => void; // For create mode: files awaiting upload
  maxFiles?: number;
  maxSize?: number; // in bytes
  disabled?: boolean;
}

export default function FileUploader({
  taskId,
  attachments = [],
  onAttachmentsChange,
  onPendingFilesChange,
  maxFiles = 20,
  maxSize = 100 * 1024 * 1024, // 100MB (via presigned URL)
  disabled = false,
}: FileUploaderProps) {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const [pendingFiles, setPendingFiles] = useState<FileWithProgress[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled) return;

      // Check total count including existing attachments
      const totalCount = attachments.length + pendingFiles.length + acceptedFiles.length;
      if (totalCount > maxFiles) {
        alert(t('uploader.maxFilesError', { max: maxFiles }));
        return;
      }

      if (taskId) {
        // Edit mode: Upload immediately with progress tracking
        for (const file of acceptedFiles) {
          const abortController = new AbortController();
          const newFile: FileWithProgress = {
            file,
            progress: 0,
            speed: 0,
            estimatedTimeRemaining: 0,
            uploading: true,
            uploaded: false,
            abortController,
          };

          setPendingFiles((prev) => [...prev, newFile]);

          try {
            const attachment = await uploadTaskAttachmentWithProgress(
              taskId,
              file,
              (uploadProgress: AttachmentUploadProgress) => {
                // Update progress in state
                setPendingFiles((prev) =>
                  prev.map((f) =>
                    f.file === file
                      ? {
                          ...f,
                          progress: uploadProgress.percentage,
                          speed: uploadProgress.speed,
                          estimatedTimeRemaining: uploadProgress.estimatedTimeRemaining,
                          attachmentId: uploadProgress.attachmentId,
                        }
                      : f
                  )
                );
              },
              abortController.signal
            );

            // Remove from pending, add to attachments
            setPendingFiles((prev) => prev.filter((f) => f.file !== file));
            onAttachmentsChange?.([...attachments, attachment]);
          } catch (error) {
            // Handle cancelled uploads
            if (error instanceof Error && error.name === 'CanceledError') {
              setPendingFiles((prev) => prev.filter((f) => f.file !== file));
              return;
            }
            // Mark as failed
            setPendingFiles((prev) =>
              prev.map((f) =>
                f.file === file
                  ? { ...f, uploading: false, error: error instanceof Error ? error.message : 'Upload failed' }
                  : f
              )
            );
          }
        }
      } else {
        // Create mode: Collect files locally
        const newFiles = acceptedFiles.map((file) => ({
          file,
          progress: 100,
          speed: 0,
          estimatedTimeRemaining: 0,
          uploading: false,
          uploaded: true,
        }));

        const updatedPending = [...pendingFiles, ...newFiles];
        setPendingFiles(updatedPending);
        onPendingFilesChange?.(updatedPending.map((f) => f.file));
      }
    },
    [taskId, attachments, pendingFiles, maxFiles, disabled, onAttachmentsChange, onPendingFilesChange]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxSize,
    multiple: true,
    disabled,
  });

  // Remove pending file (not yet uploaded)
  const removePendingFile = (index: number) => {
    const newFiles = pendingFiles.filter((_, i) => i !== index);
    setPendingFiles(newFiles);
    if (!taskId) {
      onPendingFilesChange?.(newFiles.map((f) => f.file));
    }
  };

  // Delete existing attachment
  const handleDeleteAttachment = async (attachment: TaskAttachment) => {
    if (!taskId || disabled) return;

    if (!confirm(t('uploader.deleteConfirm', { fileName: attachment.file_name }))) return;

    setDeletingIds((prev) => new Set(prev).add(attachment.id));

    try {
      await deleteTaskAttachment(taskId, attachment.id);
      onAttachmentsChange?.(attachments.filter((a) => a.id !== attachment.id));
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete attachment');
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Download attachment
  const handleDownloadAttachment = async (attachment: TaskAttachment) => {
    if (!taskId) return;

    setDownloadingIds((prev) => new Set(prev).add(attachment.id));

    try {
      const { url } = await getAttachmentDownloadUrl(taskId, attachment.id);
      // Open in new tab to trigger download
      window.open(url, '_blank');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to get download link');
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // Preview attachment
  const handlePreviewAttachment = async (attachment: TaskAttachment) => {
    if (!taskId) return;

    try {
      const { url } = await getAttachmentDownloadUrl(taskId, attachment.id);
      navigate(`/tasks/${taskId}/attachments/${attachment.id}/preview`, {
        state: {
          fileName: attachment.file_name,
          fileSize: attachment.file_size,
          downloadUrl: url,
        },
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to preview file');
    }
  };

  // Get file icon based on content type
  const getFileIcon = (contentType: string | null): string => {
    if (!contentType) return '📎';
    if (contentType.startsWith('image/')) return '🖼️';
    if (contentType.startsWith('video/')) return '🎬';
    if (contentType.startsWith('audio/')) return '🎵';
    if (contentType.includes('pdf')) return '📕';
    if (contentType.includes('word') || contentType.includes('document')) return '📄';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return '📊';
    if (contentType.includes('zip') || contentType.includes('archive')) return '📦';
    return '📎';
  };

  const totalFiles = attachments.length + pendingFiles.length;

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 bg-white/50'
        }`}
      >
        <input {...getInputProps()} />
        <div className="space-y-1">
          <svg
            className={`mx-auto h-8 w-8 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
            {disabled ? (
              <p>{t('uploader.disabled')}</p>
            ) : isDragActive ? (
              <p className="font-medium text-blue-600">{t('uploader.dropActive')}</p>
            ) : (
              <>
                <p className="font-medium">{t('uploader.dropInactive')}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('uploader.maxSize', { size: formatFileSize(maxSize) })}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File Rejections */}
      {fileRejections.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-sm">
          <p className="font-medium text-red-800">{t('uploader.rejections')}</p>
          <ul className="text-xs text-red-600 mt-1">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.name}>
                {file.name}: {errors.map((e) => e.message).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Files List */}
      {(attachments.length > 0 || pendingFiles.length > 0) && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {t('uploader.filesAttached', { count: totalFiles, plural: totalFiles !== 1 ? 's' : '' })}
          </p>

          {/* Existing Attachments */}
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md"
            >
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <span className="flex-shrink-0">{getFileIcon(attachment.content_type)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {attachment.file_name}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(attachment.file_size)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                {taskId && (
                  <>
                    {canPreview(attachment.file_name) && (
                      <button
                        onClick={() => handlePreviewAttachment(attachment)}
                        disabled={disabled}
                        className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded disabled:opacity-50"
                        title={t('uploader.preview')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadAttachment(attachment)}
                      disabled={downloadingIds.has(attachment.id) || disabled}
                      className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded disabled:opacity-50"
                      title={t('uploader.download')}
                    >
                      {downloadingIds.has(attachment.id) ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteAttachment(attachment)}
                      disabled={deletingIds.has(attachment.id) || disabled}
                      className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                      title={t('uploader.delete')}
                    >
                      {deletingIds.has(attachment.id) ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          {/* Pending Files (uploading or awaiting task creation) */}
          {pendingFiles.map((fileWithProgress, index) => (
            <div
              key={`pending-${index}`}
              className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-md"
            >
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <span className="flex-shrink-0">📎</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileWithProgress.file.name}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(fileWithProgress.file.size)}</p>
                  {fileWithProgress.uploading && (
                    <div className="mt-1 space-y-1">
                      {/* Progress bar with real percentage */}
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${fileWithProgress.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-blue-600 font-medium w-10 text-right">
                          {fileWithProgress.progress}%
                        </span>
                      </div>
                      {/* Speed and ETA */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {fileWithProgress.speed > 0
                            ? t('uploader.speed', { speed: formatFileSize(fileWithProgress.speed) })
                            : t('uploader.starting')}
                        </span>
                        {fileWithProgress.estimatedTimeRemaining > 0 && (
                          <span>
                            {fileWithProgress.estimatedTimeRemaining < 60
                              ? t('uploader.remaining', { seconds: fileWithProgress.estimatedTimeRemaining })
                              : t('uploader.remainingMin', { minutes: Math.ceil(fileWithProgress.estimatedTimeRemaining / 60) })}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {fileWithProgress.error && (
                    <p className="text-xs text-red-600 mt-1">{fileWithProgress.error}</p>
                  )}
                </div>
              </div>
              {/* Cancel button during upload */}
              {fileWithProgress.uploading && fileWithProgress.abortController && (
                <button
                  onClick={() => {
                    fileWithProgress.abortController?.abort();
                    // Also cancel on backend if we have an attachment ID
                    if (fileWithProgress.attachmentId && taskId) {
                      cancelAttachmentUpload(taskId, fileWithProgress.attachmentId).catch(() => {});
                    }
                  }}
                  className="ml-2 p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title={t('uploader.cancel')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {/* Remove button for non-uploading files */}
              {!fileWithProgress.uploading && (
                <button
                  onClick={() => removePendingFile(index)}
                  disabled={disabled}
                  className="ml-2 p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                  title={t('uploader.remove')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
